/**
 * Kiosk Header Component
 * Displays logo and optional menu/language icons
 * Based on Figma header design
 */

import React from 'react';
import { Menu, Globe } from 'lucide-react';
import { KioskUICustomization } from '../../types/kiosk-types';

interface KioskHeaderProps {
  config: KioskUICustomization;
  size: 'small' | 'large';
  showTitle?: boolean; // For 4" only
  pendingLogoUrl?: string; // Optional pending logo for live preview
  useMedia4Logo?: boolean; // Use media4Logo instead of generic logo
}

/**
 * Kiosk Header Component
 * Shows logo with optional title (4" only), menu and language icons
 */
export function KioskHeader({
  config,
  size,
  showTitle = false,
  pendingLogoUrl,
  useMedia4Logo = false,
}: KioskHeaderProps) {
  const isSmall = size === 'small';

  // Header dimensions based on Figma design
  // For 4" with title, header is taller to accommodate "Select Connector"
  const headerHeight = isSmall ? (showTitle ? '103px' : '80px') : '120px';
  const logoDimensions = isSmall
    ? { width: '95px', height: '48px' }
    : { width: '142px', height: '72px' };
  const iconSize = isSmall ? 24 : 32;

  // Get logo from config - use pending logo if available for live preview
  // For 4-inch, use media4Logo if available, otherwise fall back to generic logo
  const logoIcon = useMedia4Logo
    ? (config.icons.media4Logo || config.icons.logo)
    : config.icons.logo;
  const logoSrc = pendingLogoUrl || logoIcon?.s3Url || logoIcon?.fallbackUrl;
  const menuIcon = config.icons.menuIcon;
  const languageIcon = config.icons.languageIcon;

  return (
    <div
      className="relative flex shrink-0 flex-col"
      style={{
        height: headerHeight,
        backgroundColor: config.theme.colors.primaryBackground,
        borderBottom: `1px solid ${config.theme.colors.border}`,
      }}
    >
      {/* Single row: Logo, Title (4" only), and icons */}
      <div className="flex items-center justify-between px-4" style={{ height: isSmall && showTitle ? headerHeight : '80px' }}>
        {/* Left side - Logo and Title (4" only) */}
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div
            className="flex shrink-0 items-center justify-center"
            style={{
              width: logoDimensions.width,
              height: logoDimensions.height,
            }}
          >
            {logoSrc ? (
              <img
                key={logoSrc}
                src={logoSrc}
                alt="Logo"
                className="max-h-full max-w-full object-contain"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  mixBlendMode: 'normal', // Ensure proper rendering with transparency
                }}
                onError={(e) => {
                  console.error('Logo failed to load:', logoSrc);
                  // Fallback to placeholder if image fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div
                className="flex h-full w-full items-center justify-center rounded"
                style={{
                  backgroundColor: config.theme.colors.accentPrimary,
                  color: config.theme.colors.primaryBackground,
                  fontSize: isSmall ? '10px' : '14px',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                }}
              >
                LOGO
              </div>
            )}
          </div>

          {/* Title (4" only) - next to logo */}
          {showTitle && isSmall && (
            <h2
              className="font-semibold"
              style={{
                fontSize: '26px',
                color: config.theme.colors.primaryText,
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                lineHeight: '1',
              }}
            >
              Select Connector
            </h2>
          )}
        </div>

        {/* Right side - Language and Menu icons */}
        <div className="flex items-center gap-2">
          {/* Language icon */}
          <div
            className="flex items-center justify-center"
            style={{
              width: isSmall ? '45px' : '55px',
              height: isSmall ? '45px' : '55px',
            }}
          >
            {languageIcon?.s3Url || languageIcon?.fallbackUrl ? (
              <img
                src={languageIcon.s3Url || languageIcon.fallbackUrl}
                alt="Language"
                className="h-full w-full object-contain"
              />
            ) : (
              <Globe
                style={{
                  color: config.theme.colors.secondaryText,
                  width: iconSize,
                  height: iconSize,
                }}
              />
            )}
          </div>

          {/* Menu icon */}
          <div
            className="flex items-center justify-center"
            style={{
              width: isSmall ? '45px' : '55px',
              height: isSmall ? '45px' : '55px',
            }}
          >
            {menuIcon?.s3Url || menuIcon?.fallbackUrl ? (
              <img
                src={menuIcon.s3Url || menuIcon.fallbackUrl}
                alt="Menu"
                className="h-full w-full object-contain"
              />
            ) : (
              <Menu
                style={{
                  color: config.theme.colors.secondaryText,
                  width: iconSize,
                  height: iconSize,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
